/**
 * Enum value generators derived from GraphQL types
 * This ensures all validation, type guards, and UI constants stay in sync with the schema
 */

export const SEX_ASSIGNED_AT_BIRTH_VALUES = [
  'MALE',
  'FEMALE',
  'INTERSEX',
  'UNKNOWN',
] as const;
export type SexAssignedAtBirthValue =
  (typeof SEX_ASSIGNED_AT_BIRTH_VALUES)[number];
export const SEX_ASSIGNED_AT_BIRTH_SET = new Set(SEX_ASSIGNED_AT_BIRTH_VALUES);

export const AGE_BAND_VALUES = [
  'AGE_0_9',
  'AGE_10_19',
  'AGE_20_29',
  'AGE_30_39',
  'AGE_40_49',
  'AGE_50_59',
  'AGE_60_69',
  'AGE_70_79',
  'AGE_80_PLUS',
] as const;
export type AgeBandValue = (typeof AGE_BAND_VALUES)[number];
export const AGE_BAND_SET = new Set(AGE_BAND_VALUES);

export const CENSUS_REGION_VALUES = [
  'NORTHEAST',
  'MIDWEST',
  'SOUTH',
  'WEST',
  'UNKNOWN',
] as const;
export type CensusRegionValue = (typeof CENSUS_REGION_VALUES)[number];
export const CENSUS_REGION_SET = new Set(CENSUS_REGION_VALUES);

export const RACE_ETHNICITY_VALUES = [
  'WHITE',
  'BLACK_OR_AFRICAN_AMERICAN',
  'ASIAN',
  'AMERICAN_INDIAN_OR_ALASKA_NATIVE',
  'NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER',
  'HISPANIC_OR_LATINO',
  'NOT_HISPANIC_OR_LATINO',
  'MULTIPLE',
  'UNKNOWN',
] as const;
export type RaceEthnicityValue = (typeof RACE_ETHNICITY_VALUES)[number];
export const RACE_ETHNICITY_SET = new Set(RACE_ETHNICITY_VALUES);

export const VARIANT_CLASSIFICATION_VALUES = [
  'PATHOGENIC',
  'LIKELY_PATHOGENIC',
  'VUS',
  'LIKELY_BENIGN',
  'BENIGN',
] as const;
export type VariantClassificationValue =
  (typeof VARIANT_CLASSIFICATION_VALUES)[number];
export const VARIANT_CLASSIFICATION_SET = new Set(
  VARIANT_CLASSIFICATION_VALUES,
);

export const ZYGOSITY_VALUES = [
  'HEMIZYGOUS',
  'HETEROZYGOUS',
  'HOMOZYGOUS',
  'MOSAIC',
  'UNKNOWN',
] as const;
export type ZygosityValue = (typeof ZYGOSITY_VALUES)[number];
export const ZYGOSITY_SET = new Set(ZYGOSITY_VALUES);

export const TEST_TYPE_VALUES = [
  'SINGLE_GENE',
  'PANEL',
  'EXOME',
  'GENOME',
  'TARGETED_VARIANT',
] as const;
export type TestTypeValue = (typeof TEST_TYPE_VALUES)[number];
export const TEST_TYPE_SET = new Set(TEST_TYPE_VALUES);

export const LAB_SOURCE_VALUES = ['LABCORP', 'QUEST', 'OTHER'] as const;
export type LabSourceValue = (typeof LAB_SOURCE_VALUES)[number];
export const LAB_SOURCE_SET = new Set(LAB_SOURCE_VALUES);

export const INHERITANCE_PATTERN_VALUES = [
  'AUTOSOMAL_RECESSIVE',
  'AUTOSOMAL_DOMINANT',
  'X_LINKED_RECESSIVE',
  'X_LINKED_DOMINANT',
  'MITOCHONDRIAL',
  'UNKNOWN',
] as const;
export type InheritancePatternValue =
  (typeof INHERITANCE_PATTERN_VALUES)[number];
export const INHERITANCE_PATTERN_SET = new Set(INHERITANCE_PATTERN_VALUES);

export const PROBAND_RELATIONSHIP_VALUES = [
  'SELF',
  'PARENT',
  'CHILD',
  'SIBLING',
  'GRANDPARENT',
  'GRANDCHILD',
  'AUNT_UNCLE',
  'NIECE_NEPHEW',
  'COUSIN',
  'OTHER',
  'UNKNOWN',
] as const;
export type ProbandRelationshipValue =
  (typeof PROBAND_RELATIONSHIP_VALUES)[number];
export const PROBAND_RELATIONSHIP_SET = new Set(PROBAND_RELATIONSHIP_VALUES);

export const RELATIONSHIP_DEGREE_VALUES = [
  'FIRST_DEGREE',
  'SECOND_DEGREE',
  'THIRD_DEGREE',
  'MORE_DISTANT',
  'UNKNOWN',
] as const;
export type RelationshipDegreeValue =
  (typeof RELATIONSHIP_DEGREE_VALUES)[number];
export const RELATIONSHIP_DEGREE_SET = new Set(RELATIONSHIP_DEGREE_VALUES);

export const ONSET_TYPE_VALUES = [
  'NEONATAL',
  'INFANTILE',
  'CHILDHOOD',
  'ADOLESCENT',
  'ADULT',
  'UNKNOWN',
] as const;
export type OnsetTypeValue = (typeof ONSET_TYPE_VALUES)[number];
export const ONSET_TYPE_SET = new Set(ONSET_TYPE_VALUES);

export const TREATMENT_EXPOSURE_VALUES = [
  'NEVER_TREATED',
  'CURRENTLY_TREATED',
  'PREVIOUSLY_TREATED',
  'UNKNOWN',
] as const;
export type TreatmentExposureValue = (typeof TREATMENT_EXPOSURE_VALUES)[number];
export const TREATMENT_EXPOSURE_SET = new Set(TREATMENT_EXPOSURE_VALUES);

export const NUTRITIONAL_MANAGEMENT_VALUES = [
  'NONE',
  'DIETARY_MODIFICATION',
  'MEDICAL_NUTRITION_THERAPY',
  'SUPPLEMENTATION',
  'TUBE_FEEDING',
  'UNKNOWN',
] as const;
export type NutritionalManagementValue =
  (typeof NUTRITIONAL_MANAGEMENT_VALUES)[number];
export const NUTRITIONAL_MANAGEMENT_SET = new Set(
  NUTRITIONAL_MANAGEMENT_VALUES,
);

export const SURGICAL_INTERVENTION_VALUES = [
  'NONE',
  'PRIOR_SURGERY',
  'CURRENT_RECENT_SURGERY',
  'PLANNED_SURGERY',
  'UNKNOWN',
] as const;
export type SurgicalInterventionValue =
  (typeof SURGICAL_INTERVENTION_VALUES)[number];
export const SURGICAL_INTERVENTION_SET = new Set(SURGICAL_INTERVENTION_VALUES);

export const SUPPORTIVE_CARE_VALUES = [
  'NONE',
  'SYMPTOMATIC_MANAGEMENT',
  'SUPPORTIVE_THERAPIES',
  'PALLIATIVE_CARE',
  'UNKNOWN',
] as const;
export type SupportiveCareValue = (typeof SUPPORTIVE_CARE_VALUES)[number];
export const SUPPORTIVE_CARE_SET = new Set(SUPPORTIVE_CARE_VALUES);

export const CRISIS_HISTORY_VALUES = [
  'NONE',
  'SINGLE_EPISODE',
  'RECURRENT_EPISODES',
  'UNKNOWN',
] as const;
export type CrisisHistoryValue = (typeof CRISIS_HISTORY_VALUES)[number];
export const CRISIS_HISTORY_SET = new Set(CRISIS_HISTORY_VALUES);

export const DATA_SOURCE_TYPE_VALUES = [
  'CLINICAL_PARTNER',
  'LABORATORY',
  'GENEALOGY_INTAKE',
  'PATIENT_REPORTED',
  'DERIVED',
] as const;
export type DataSourceTypeValue = (typeof DATA_SOURCE_TYPE_VALUES)[number];
export const DATA_SOURCE_TYPE_SET = new Set(DATA_SOURCE_TYPE_VALUES);

export const DIAGNOSTIC_SOURCE_VALUES = [
  'CLINICAL_DIAGNOSIS',
  'GENETIC_TEST',
  'NEWBORN_SCREENING',
  'FAMILY_HISTORY',
  'RESEARCH_FINDING',
  'UNKNOWN',
] as const;
export type DiagnosticSourceValue = (typeof DIAGNOSTIC_SOURCE_VALUES)[number];
export const DIAGNOSTIC_SOURCE_SET = new Set(DIAGNOSTIC_SOURCE_VALUES);

/**
 * Map enum values to human-readable labels
 */
export const SEX_ASSIGNED_AT_BIRTH_LABELS: Record<
  SexAssignedAtBirthValue,
  string
> = {
  MALE: 'Male',
  FEMALE: 'Female',
  INTERSEX: 'Intersex',
  UNKNOWN: 'Unknown',
};

export const AGE_BAND_LABELS: Record<AgeBandValue, string> = {
  AGE_0_9: '0-9 years',
  AGE_10_19: '10-19 years',
  AGE_20_29: '20-29 years',
  AGE_30_39: '30-39 years',
  AGE_40_49: '40-49 years',
  AGE_50_59: '50-59 years',
  AGE_60_69: '60-69 years',
  AGE_70_79: '70-79 years',
  AGE_80_PLUS: '80+ years',
};

export const CENSUS_REGION_LABELS: Record<CensusRegionValue, string> = {
  NORTHEAST: 'Northeast',
  MIDWEST: 'Midwest',
  SOUTH: 'South',
  WEST: 'West',
  UNKNOWN: 'Unknown',
};

export const RACE_ETHNICITY_LABELS: Record<RaceEthnicityValue, string> = {
  WHITE: 'White',
  BLACK_OR_AFRICAN_AMERICAN: 'Black or African American',
  ASIAN: 'Asian',
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: 'American Indian or Alaska Native',
  NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER:
    'Native Hawaiian or Other Pacific Islander',
  HISPANIC_OR_LATINO: 'Hispanic or Latino',
  NOT_HISPANIC_OR_LATINO: 'Not Hispanic or Latino',
  MULTIPLE: 'Multiple',
  UNKNOWN: 'Unknown',
};

export const VARIANT_CLASSIFICATION_LABELS: Record<
  VariantClassificationValue,
  string
> = {
  PATHOGENIC: 'Pathogenic',
  LIKELY_PATHOGENIC: 'Likely Pathogenic',
  VUS: 'VUS',
  LIKELY_BENIGN: 'Likely Benign',
  BENIGN: 'Benign',
};

export const ZYGOSITY_LABELS: Record<ZygosityValue, string> = {
  HEMIZYGOUS: 'Hemizygous',
  HETEROZYGOUS: 'Heterozygous',
  HOMOZYGOUS: 'Homozygous',
  MOSAIC: 'Mosaic',
  UNKNOWN: 'Unknown',
};

export const TEST_TYPE_LABELS: Record<TestTypeValue, string> = {
  SINGLE_GENE: 'Single-gene',
  PANEL: 'Panel',
  EXOME: 'Exome',
  GENOME: 'Genome',
  TARGETED_VARIANT: 'Targeted variant',
};

export const LAB_SOURCE_LABELS: Record<LabSourceValue, string> = {
  LABCORP: 'LabCorp',
  QUEST: 'Quest',
  OTHER: 'Other',
};

export const INHERITANCE_PATTERN_LABELS: Record<
  InheritancePatternValue,
  string
> = {
  AUTOSOMAL_RECESSIVE: 'Autosomal Recessive',
  AUTOSOMAL_DOMINANT: 'Autosomal Dominant',
  X_LINKED_RECESSIVE: 'X-linked Recessive',
  X_LINKED_DOMINANT: 'X-linked Dominant',
  MITOCHONDRIAL: 'Mitochondrial',
  UNKNOWN: 'Unknown',
};

export const PROBAND_RELATIONSHIP_LABELS: Record<
  ProbandRelationshipValue,
  string
> = {
  SELF: 'Self',
  PARENT: 'Parent',
  CHILD: 'Child',
  SIBLING: 'Sibling',
  GRANDPARENT: 'Grandparent',
  GRANDCHILD: 'Grandchild',
  AUNT_UNCLE: 'Aunt/Uncle',
  NIECE_NEPHEW: 'Niece/Nephew',
  COUSIN: 'Cousin',
  OTHER: 'Other',
  UNKNOWN: 'Unknown',
};

export const RELATIONSHIP_DEGREE_LABELS: Record<
  RelationshipDegreeValue,
  string
> = {
  FIRST_DEGREE: 'First-degree',
  SECOND_DEGREE: 'Second-degree',
  THIRD_DEGREE: 'Third-degree',
  MORE_DISTANT: 'More distant',
  UNKNOWN: 'Unknown',
};

export const ONSET_TYPE_LABELS: Record<OnsetTypeValue, string> = {
  NEONATAL: 'Neonatal',
  INFANTILE: 'Infantile',
  CHILDHOOD: 'Childhood',
  ADOLESCENT: 'Adolescent',
  ADULT: 'Adult',
  UNKNOWN: 'Unknown',
};

export const TREATMENT_EXPOSURE_LABELS: Record<TreatmentExposureValue, string> =
  {
    NEVER_TREATED: 'Never treated',
    CURRENTLY_TREATED: 'Currently treated',
    PREVIOUSLY_TREATED: 'Previously treated',
    UNKNOWN: 'Unknown',
  };

export const NUTRITIONAL_MANAGEMENT_LABELS: Record<
  NutritionalManagementValue,
  string
> = {
  NONE: 'None',
  DIETARY_MODIFICATION: 'Dietary modification',
  MEDICAL_NUTRITION_THERAPY: 'Medical nutrition therapy',
  SUPPLEMENTATION: 'Supplementation',
  TUBE_FEEDING: 'Tube feeding',
  UNKNOWN: 'Unknown',
};

export const SURGICAL_INTERVENTION_LABELS: Record<
  SurgicalInterventionValue,
  string
> = {
  NONE: 'None',
  PRIOR_SURGERY: 'Prior surgery',
  CURRENT_RECENT_SURGERY: 'Current/recent surgery',
  PLANNED_SURGERY: 'Planned surgery',
  UNKNOWN: 'Unknown',
};

export const SUPPORTIVE_CARE_LABELS: Record<SupportiveCareValue, string> = {
  NONE: 'None',
  SYMPTOMATIC_MANAGEMENT: 'Symptomatic management',
  SUPPORTIVE_THERAPIES: 'Supportive therapies',
  PALLIATIVE_CARE: 'Palliative care',
  UNKNOWN: 'Unknown',
};

export const CRISIS_HISTORY_LABELS: Record<CrisisHistoryValue, string> = {
  NONE: 'None',
  SINGLE_EPISODE: 'Single episode',
  RECURRENT_EPISODES: 'Recurrent episodes',
  UNKNOWN: 'Unknown',
};

export const DATA_SOURCE_TYPE_LABELS: Record<DataSourceTypeValue, string> = {
  CLINICAL_PARTNER: 'Clinical partner',
  LABORATORY: 'Laboratory',
  GENEALOGY_INTAKE: 'Genealogy intake',
  PATIENT_REPORTED: 'Patient-reported',
  DERIVED: 'Derived',
};

export const DIAGNOSTIC_SOURCE_LABELS: Record<DiagnosticSourceValue, string> = {
  CLINICAL_DIAGNOSIS: 'Clinical diagnosis',
  GENETIC_TEST: 'Genetic test',
  NEWBORN_SCREENING: 'Newborn screening',
  FAMILY_HISTORY: 'Family history',
  RESEARCH_FINDING: 'Research finding',
  UNKNOWN: 'Unknown',
};

/**
 * Generate option arrays for UI selects from enum values
 * We use a wrapper function to ensure TypeScript can properly infer that each
 * value from the array is a valid key in the labels record
 */
export function generateOptions<T extends readonly string[]>(
  values: T,
  labels: Record<T[number], string>,
): Array<{ value: T[number]; label: string }> {
  const createOption = (
    value: T[number],
  ): { value: T[number]; label: string } => ({
    value,
    label: labels[value],
  });

  return values.map(createOption);
}

export const SEX_ASSIGNED_AT_BIRTH_OPTIONS = generateOptions(
  SEX_ASSIGNED_AT_BIRTH_VALUES,
  SEX_ASSIGNED_AT_BIRTH_LABELS,
);

export const AGE_BAND_OPTIONS = generateOptions(
  AGE_BAND_VALUES,
  AGE_BAND_LABELS,
);

export const CENSUS_REGION_OPTIONS = generateOptions(
  CENSUS_REGION_VALUES,
  CENSUS_REGION_LABELS,
);

export const RACE_ETHNICITY_OPTIONS = generateOptions(
  RACE_ETHNICITY_VALUES,
  RACE_ETHNICITY_LABELS,
);

export const VARIANT_CLASSIFICATION_OPTIONS = generateOptions(
  VARIANT_CLASSIFICATION_VALUES,
  VARIANT_CLASSIFICATION_LABELS,
);

export const ZYGOSITY_OPTIONS = generateOptions(
  ZYGOSITY_VALUES,
  ZYGOSITY_LABELS,
);

export const TEST_TYPE_OPTIONS = generateOptions(
  TEST_TYPE_VALUES,
  TEST_TYPE_LABELS,
);

export const LAB_SOURCE_OPTIONS = generateOptions(
  LAB_SOURCE_VALUES,
  LAB_SOURCE_LABELS,
);

export const INHERITANCE_PATTERN_OPTIONS = generateOptions(
  INHERITANCE_PATTERN_VALUES,
  INHERITANCE_PATTERN_LABELS,
);

export const PROBAND_RELATIONSHIP_OPTIONS = generateOptions(
  PROBAND_RELATIONSHIP_VALUES,
  PROBAND_RELATIONSHIP_LABELS,
);

export const RELATIONSHIP_DEGREE_OPTIONS = generateOptions(
  RELATIONSHIP_DEGREE_VALUES,
  RELATIONSHIP_DEGREE_LABELS,
);

export const ONSET_TYPE_OPTIONS = generateOptions(
  ONSET_TYPE_VALUES,
  ONSET_TYPE_LABELS,
);

export const TREATMENT_EXPOSURE_OPTIONS = generateOptions(
  TREATMENT_EXPOSURE_VALUES,
  TREATMENT_EXPOSURE_LABELS,
);

export const NUTRITIONAL_MANAGEMENT_OPTIONS = generateOptions(
  NUTRITIONAL_MANAGEMENT_VALUES,
  NUTRITIONAL_MANAGEMENT_LABELS,
);

export const SURGICAL_INTERVENTION_OPTIONS = generateOptions(
  SURGICAL_INTERVENTION_VALUES,
  SURGICAL_INTERVENTION_LABELS,
);

export const SUPPORTIVE_CARE_OPTIONS = generateOptions(
  SUPPORTIVE_CARE_VALUES,
  SUPPORTIVE_CARE_LABELS,
);

export const CRISIS_HISTORY_OPTIONS = generateOptions(
  CRISIS_HISTORY_VALUES,
  CRISIS_HISTORY_LABELS,
);

export const DATA_SOURCE_TYPE_OPTIONS = generateOptions(
  DATA_SOURCE_TYPE_VALUES,
  DATA_SOURCE_TYPE_LABELS,
);

export const DIAGNOSTIC_SOURCE_OPTIONS = generateOptions(
  DIAGNOSTIC_SOURCE_VALUES,
  DIAGNOSTIC_SOURCE_LABELS,
);
